// Demo-mode mirror of private.leaderboard_at (supabase/schemas/30_gavion_goals.sql; « Entrants »:
// 82_gavion_inbound_ranking.sql). The demo always uses
// America/Toronto (the SQL default when no zone is configured).
import type { FunnelCall, FunnelDeal } from "../../domain/funnel/dealFunnel";
import { periodBounds } from "../../domain/funnel/period";
import { CONTACT_OUTCOMES } from "../../domain/funnel/prospectingFunnel";
import {
  addDays,
  torontoDate,
  torontoDowHour,
} from "../../domain/time/toronto";
import type {
  LeaderboardPeriod,
  LeaderboardReport,
  LeaderboardRow,
} from "../types";

export type LeaderboardSale = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  administrator?: boolean | null;
  disabled?: boolean | null;
};

export const leaderboardRange = (period: LeaderboardPeriod, now: Date) => {
  const today = torontoDate(now);
  const from =
    period === "today"
      ? today
      : period === "week"
        ? addDays(today, 1 - torontoDowHour(now).dow)
        : `${today.slice(0, 7)}-01`;
  return { from, to: today };
};

const byName = (a: LeaderboardRow, b: LeaderboardRow) =>
  a.name < b.name ? -1 : a.name > b.name ? 1 : 0; // SQL: collate "C"

/** An incoming call answered in the CRM (claimed_at) or billed by VoIP.ms; a missed call has neither. */
type LeaderboardCall = FunnelCall & {
  claimed_at?: string | null;
  billsec_s?: number | null;
};

export const computeLeaderboard = (input: {
  calls: readonly LeaderboardCall[];
  deals: readonly FunnelDeal[];
  sales: readonly LeaderboardSale[];
  period: LeaderboardPeriod;
  now: Date;
}): LeaderboardReport => {
  const { from, to } = leaderboardRange(input.period, input.now);
  const { start, end } = periodBounds(from, to);
  const inPeriod = (iso: string | null | undefined) => {
    if (!iso) return false;
    const t = Date.parse(iso);
    return t >= start && t < end;
  };
  const rows: LeaderboardRow[] = [];
  for (const s of input.sales) {
    if (s.disabled) continue;
    const calls = input.calls.filter(
      (c) =>
        c.sales_id === s.id &&
        c.direction === "outbound" &&
        c.outcome !== "skipped" &&
        inPeriod(c.dialed_at),
    );
    const inbound = input.calls.filter(
      (c) =>
        c.sales_id === s.id &&
        c.direction === "inbound" &&
        (c.claimed_at != null || (c.billsec_s ?? 0) > 0) &&
        inPeriod(c.dialed_at),
    );
    const won = input.deals.filter(
      (d) =>
        d.sales_id === s.id &&
        d.stage === "gagne" &&
        !d.archived_at &&
        inPeriod(d.won_at),
    ).length;
    const isAdmin = s.role === "admin" || s.administrator === true;
    if (isAdmin && calls.length === 0 && inbound.length === 0 && won === 0)
      continue;
    const talked = [...calls, ...inbound];
    rows.push({
      sales_id: s.id,
      name: `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim(),
      dials: calls.length,
      conversations: talked.filter((c) => CONTACT_OUTCOMES.has(c.outcome ?? ""))
        .length,
      demos: talked.filter((c) => c.outcome === "demo_booked").length,
      deals_won: won,
      inbound: inbound.length,
    });
  }
  rows.sort(
    (a, b) =>
      b.deals_won - a.deals_won ||
      b.demos - a.demos ||
      b.conversations - a.conversations ||
      b.dials - a.dials ||
      byName(a, b) ||
      a.sales_id - b.sales_id,
  );
  return {
    period: input.period,
    from,
    to,
    timezone: "America/Toronto",
    rows,
  };
};
